import {
  IAuthorizationService,
  YANDEX_CLOUD_PROVIDER,
  YANDEX_CLOUD_ERROR_KEY,
  YANDEX_CLOUD_STORAGE_KEY,
  type YandexCloudTransition,
} from '../../types/features/auth/auth-types';
import { IAppLoaderStorage, StorageKey } from '../../types/utils/storage-types';
import { setCookie } from '../../../../shared/infrastructure/utils/cookie-utils';
import { datesUtil } from '../../../../shared/infrastructure/utils/dates-util';
import { UserInfo } from '../../types/features/auth/auth-contracts';

const AUTH_CHECK_INTERVAL_MS = 30 * 1000;
const TOKEN_REFRESH_TIMEOUT_MS = 5 * 1000;
const MIN_EXPIRES_IN_S = 180;
const DEFAULT_EXPIRES_IN_S = 7200;
const TOKEN_REFRESH_BUFFER_S = 120;
const HASH_ROUTE_TTL_MS = 3 * 60 * 1000;
const CROSS_DOMAIN_COOKIE_TTL_MS = 14 * 86400 * 1000;

class AuthorizationService implements IAuthorizationService {
  appStorage: IAppLoaderStorage;

  private authCheckInterval?: NodeJS.Timeout;
  private tokenRefreshInterval?: NodeJS.Timeout;
  private tokenRefreshTimeout?: NodeJS.Timeout;

  constructor(appStorage: IAppLoaderStorage) {
    this.appStorage = appStorage;
  }

  setupAuthorization(accountID: AccountID) {
    this.saveAuthCookies(accountID);
  }

  clearAuthorization() {
    this.appStorage.clearEntry(StorageKey.SKOROZVON_LAST_USER);
    this.clearAuthCookies();
    window.authToken = undefined as unknown as AccessTokenBody;
    window.skorozvonAuthToken = undefined as unknown as AccessTokenBody;
  }

  private clearAuthCookies() {
    setCookie('auth_token', '', {
      domain: ('.' + this.appStorage.domain) as Host,
      path: '/',
      'max-age': -1,
    });
    setCookie('account_id', '', {
      domain: ('.' + this.appStorage.domain) as Host,
      path: '/',
      'max-age': -1,
    });
  }

  setAuthToken(token: AccessTokenBody) {
    window.authToken = token;
    window.skorozvonAuthToken = token;
  }

  setAppServers(servers: Url[]) {
    window.appServers = servers;
  }

  setLocales(locales: string[]) {
    window.locales = locales;
  }

  setRejectReason(reason: string) {
    window.REJECT_REASON = reason;
    setCookie('reject_reason', reason, {
      domain: ('.' + this.appStorage.domain) as Host,
      path: '/',
    });
  }

  getRejectReason(): string | null {
    const reason = this.readRejectReasonCookie() ?? window.REJECT_REASON;
    window.REJECT_REASON = '';
    this.clearRejectReasonCookies();
    return reason ? reason : null;
  }

  private readRejectReasonCookie(): string | undefined {
    const prefix = 'reject_reason=';

    return document.cookie
      .split(';')
      .map(entry => entry.trim())
      .filter(entry => entry.startsWith(prefix))
      .map(entry => decodeURIComponent(entry.slice(prefix.length)))
      .find(value => value.length > 0);
  }

  private clearRejectReasonCookies() {
    setCookie('reject_reason', '', {
      domain: ('.' + this.appStorage.domain) as Host,
      path: '/',
      'max-age': -1,
    });
    setCookie('reject_reason', '', { path: '/', 'max-age': -1 });
  }

  saveHashRoute() {
    const hash = window.location.hash;
    if (hash.length > 0 && !/^\/*#\/$/.test(hash)) {
      const expires = new Date(Date.now() + HASH_ROUTE_TTL_MS);
      document.cookie = 'hash_route=' + hash + ';expires=' + expires.toUTCString();
    }
  }

  applyCrossDomainCookieFromQuery() {
    const match = location.href.toString().match(/\bc=([^&]+)&*/);
    if (!match) {
      return;
    }

    const cookieString = decodeURIComponent(match[1]);
    const expires = new Date(Date.now() + HASH_ROUTE_TTL_MS + CROSS_DOMAIN_COOKIE_TTL_MS);
    document.cookie =
      cookieString +
      ';expires=' +
      expires.toUTCString() +
      ';domain=.' +
      location.host.split('.').splice(1).join('.');
    window.location.href = location.href
      .replace(/\b(c=[^&#]+&*)/, '')
      .replace(/\/\?#/, '/#')
      .replace(/\/&/, '/?')
      .replace(/#\/#/, '#/');
  }

  saveUserInfo(data: UserInfo) {
    this.appStorage.addEntry(StorageKey.SKOROZVON_LAST_USER, data);
  }

  loadUserInfo(): UserInfo | null {
    const raw = this.appStorage.getEntry(StorageKey.SKOROZVON_LAST_USER);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as UserInfo;
    } catch {
      return null;
    }
  }

  getAccessToken(): string | null {
    return new URLSearchParams(location.search).get('access_token');
  }

  getManifestPath(): string {
    return (
      new URLSearchParams(location.search).get('manifest_path') ||
      window.product?.manifest_path ||
      '/assets/manifest.json'
    );
  }

  clearURLAccessToken() {
    if (location.search.includes('access_token=')) {
      window.history.replaceState({}, document.title, location.pathname);
    }
  }

  consumeYandexCloudTransition(): YandexCloudTransition {
    const searchParams = new URLSearchParams(location.search);
    const { path: hashPath, params: hashParams } = this.getHashParams();
    const searchHasYandexCloudProvider = searchParams.get('provider') === YANDEX_CLOUD_PROVIDER;
    const hashHasYandexCloudProvider = hashParams.get('provider') === YANDEX_CLOUD_PROVIDER;
    const isYandexCloudProvider = searchHasYandexCloudProvider || hashHasYandexCloudProvider;
    const providerParams = searchHasYandexCloudProvider ? searchParams : hashParams;
    const queryToken = isYandexCloudProvider ? providerParams.get('token') ?? undefined : undefined;
    const searchAuthError = searchParams.get('auth_error') ?? undefined;
    const hashAuthError = hashParams.get('auth_error') ?? undefined;
    const authError = this.isYandexCloudAuthError(searchAuthError)
      ? searchAuthError
      : hashAuthError;
    const isYandexCloudAuthError = !!authError && this.isYandexCloudAuthError(authError);
    let token: string | undefined;

    if (isYandexCloudProvider) {
      token = queryToken;
      this.clearYandexCloudTransition();
    } else if (isYandexCloudAuthError) {
      this.clearYandexCloudTransition();
    } else {
      token = this.loadYandexCloudToken();
    }

    this.clearYandexCloudURLParams(searchParams, hashPath, hashParams);

    return {
      isActive: (isYandexCloudProvider && !isYandexCloudAuthError) || !!token,
      isFromURL: isYandexCloudProvider,
      token,
      authError: isYandexCloudAuthError ? authError : undefined,
    };
  }

  saveYandexCloudTransition(token: string) {
    try {
      sessionStorage.setItem(
        YANDEX_CLOUD_STORAGE_KEY,
        JSON.stringify({ provider: YANDEX_CLOUD_PROVIDER, token }),
      );
    } catch {
      return;
    }
  }

  clearYandexCloudTransition() {
    try {
      sessionStorage.removeItem(YANDEX_CLOUD_STORAGE_KEY);
    } catch {
      return;
    }
  }

  private loadYandexCloudToken(): string | undefined {
    try {
      const stored = JSON.parse(sessionStorage.getItem(YANDEX_CLOUD_STORAGE_KEY) ?? '{}');
      if (stored.provider === YANDEX_CLOUD_PROVIDER && typeof stored.token === 'string') {
        return stored.token;
      }
    } catch {
      this.clearYandexCloudTransition();
    }

    return undefined;
  }

  private getHashParams(): { path: string; params: URLSearchParams } {
    const queryStart = location.hash.indexOf('?');
    if (queryStart === -1) {
      return { path: location.hash, params: new URLSearchParams() };
    }

    return {
      path: location.hash.slice(0, queryStart),
      params: new URLSearchParams(location.hash.slice(queryStart + 1)),
    };
  }

  private isYandexCloudAuthError(error?: string): boolean {
    return !!error && Object.prototype.hasOwnProperty.call(YANDEX_CLOUD_ERROR_KEY, error);
  }

  private clearYandexCloudURLParams(
    searchParams: URLSearchParams,
    hashPath: string,
    hashParams: URLSearchParams,
  ) {
    const searchChanged = this.clearYandexCloudParams(searchParams);
    const hashChanged = this.clearYandexCloudParams(hashParams);

    if (!searchChanged && !hashChanged) {
      return;
    }

    const searchQuery = searchParams.toString();
    const hashQuery = hashParams.toString();
    const nextHash = hashPath + (hashQuery ? `?${hashQuery}` : '');
    const nextUrl = location.pathname + (searchQuery ? `?${searchQuery}` : '') + nextHash;
    window.history.replaceState({}, document.title, nextUrl);
  }

  private clearYandexCloudParams(params: URLSearchParams): boolean {
    let changed = false;

    if (params.get('provider') === YANDEX_CLOUD_PROVIDER) {
      params.delete('provider');
      params.delete('token');
      changed = true;
    }

    if (this.isYandexCloudAuthError(params.get('auth_error') ?? undefined)) {
      params.delete('auth_error');
      changed = true;
    }

    return changed;
  }

  startAuthCheckInterval(callback: () => void) {
    if (this.authCheckInterval) {
      clearInterval(this.authCheckInterval);
    }

    this.authCheckInterval = setInterval(callback, AUTH_CHECK_INTERVAL_MS);
  }

  startTokenRefreshInterval(expiresIn: number, callback: () => void) {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    const normalizedExpiresIn =
      expiresIn > 0 ? Math.max(expiresIn, MIN_EXPIRES_IN_S) : DEFAULT_EXPIRES_IN_S;

    this.tokenRefreshInterval = setInterval(
      callback,
      (normalizedExpiresIn - TOKEN_REFRESH_BUFFER_S) * 1000,
    );
  }

  startTokenRefreshTimeout(callback: () => void) {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
    }

    this.tokenRefreshTimeout = setTimeout(callback, TOKEN_REFRESH_TIMEOUT_MS);
  }

  clearTimeouts() {
    if (this.authCheckInterval) {
      clearInterval(this.authCheckInterval);
      this.authCheckInterval = undefined;
    }

    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = undefined;
    }

    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = undefined;
    }
  }

  private saveAuthCookies(accountID: AccountID) {
    if (!this.appStorage.authToken) {
      return;
    }

    setCookie('auth_token', this.appStorage.authToken, {
      domain: ('.' + this.appStorage.domain) as Host,
      path: '/',
      expires: datesUtil.addHours(new Date(), 2),
    });

    setCookie('account_id', accountID, {
      domain: ('.' + this.appStorage.domain) as Host,
      path: '/',
    });
  }
}

export { AuthorizationService };
